import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, HelpCircle } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { EmptyState } from '@/components/EmptyState';
import type { SkillGapsResponse } from './types';

/**
 * Drawer「概要」タブ (`GET /employees/:employeeId/skills/gaps`、設計書§1-1/§3-2)。
 * 不足スキルと、求められる基準/本人の現在水準それぞれの行動記述を対比表示する。
 * ⚠️ APIレスポンスは snake_case (実装側連絡により訂正、./types.ts参照)。
 */
export function OverviewSection({ employeeId, roleProfileId, onForbidden }: {
  employeeId: string;
  roleProfileId: string;
  onForbidden: () => void;
}) {
  const { data, isLoading, isError, error } = useQuery<SkillGapsResponse>({
    queryKey: ['employee-skill-gaps', employeeId, roleProfileId],
    queryFn: () => apiFetch<SkillGapsResponse>(`/employees/${employeeId}/skills/gaps?role_profile_id=${encodeURIComponent(roleProfileId)}`),
    enabled: !!employeeId && !!roleProfileId,
    retry: false,
  });

  useEffect(() => {
    if (isError && error instanceof ApiClientError && error.status === 403) onForbidden();
  }, [isError, error, onForbidden]);

  if (!roleProfileId) {
    return <p className="text-sm text-text-sub">人材像が指定されていません。ロースターのセルから開いてください。</p>;
  }
  if (isLoading) return <div role="status" className="text-text-sub">読み込み中…</div>;
  if (isError) return <p role="alert" className="text-sm text-alert-text">不足スキルの取得に失敗しました。</p>;

  const gaps = data?.gaps ?? [];
  if (gaps.length === 0) {
    return <EmptyState message="不足しているスキルはありません" description="この人材像に必要なスキルはすべて充足しています。" />;
  }

  return (
    <ul className="space-y-2">
      {gaps.map((g) => (
        <li key={g.skill_id} className="border border-surface-border rounded-lg bg-white p-3">
          <div className="flex items-center gap-1.5 text-sm font-semibold text-text-body break-words">
            {g.undeterminable
              ? <HelpCircle size={14} className="text-text-sub shrink-0" aria-hidden />
              : <AlertTriangle size={14} className="text-alert-text shrink-0" aria-hidden />}
            {g.skill_label ?? g.skill_id}
            {g.undeterminable && <span className="text-xs font-normal text-text-sub">(判定不能)</span>}
          </div>
          <dl className="mt-1.5 text-xs text-text-sub space-y-1">
            <div>
              <dt className="inline font-semibold text-text-body">求められる基準: </dt>
              <dd className="inline">{g.required_skill_level_label ?? '—'}</dd>
              {g.required_behavior_description && <p className="mt-0.5 break-words">{g.required_behavior_description}</p>}
            </div>
            <div>
              <dt className="inline font-semibold text-text-body">現在の水準: </dt>
              <dd className="inline">{g.current_skill_level_label ?? '未登録'}</dd>
              {g.current_behavior_description && <p className="mt-0.5 break-words">{g.current_behavior_description}</p>}
            </div>
          </dl>
        </li>
      ))}
    </ul>
  );
}
